import {
    ArrowPathIcon,
    CheckCircleIcon,
    GlobeAltIcon,
    PhoneIcon,
    PhotoIcon,
    TagIcon,
    UserCircleIcon,
    UsersIcon as UsersIconSolid,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import PlaceAutocompleteInput from "../PlacesAutocomplete/PlacesAutocomplete.js";

const EDIT_MODAL_PUBLISH_OPTIONS = [
    {
        value: "Full Trust Circle",
        label: "Entire Trust Circle",
        icon: UsersIconSolid,
    },
    {
        value: "Specific Trust Circles",
        label: "Specific Trust Circles",
        icon: UserCircleIcon,
    },
    { value: "Public", label: "Public", icon: GlobeAltIcon },
];

const EditModalStarDisplay = ({
    active,
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    if (active) {
        return (
            <svg
                className="profile-edit-modal-star-icon filled"
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                aria-hidden="true"
                viewBox="0 0 20 20"
                fill="currentColor"
                width={24}
                height={24}
            >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.388 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.388-2.46a1 1 0 00-1.176 0l-3.388 2.46c-.784.57-1.838-.196-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.388-2.46c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.967z" />
            </svg>
        );
    }
    return (
        <svg
            className="profile-edit-modal-star-icon"
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            aria-hidden="true"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            width={24}
            height={24}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.388 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.388-2.46a1 1 0 00-1.176 0l-3.388 2.46c-.784.57-1.838-.196-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.388-2.46c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.967z"
            />
        </svg>
    );
};

const EditRecommendationModal = ({
    isOpen,
    onClose,
    recommendationToEdit,
    onSaveSuccess,
    userEmail,
    clerkUserId,
    apiBaseUrl,
    userTrustCirclesProp,
    trustCirclesLoadingProp,
    trustCirclesErrorProp,
    fetchUserTrustCirclesFunc,
}) => {
    const [businessName, setBusinessName] = useState("");
    const [recommenderMessage, setRecommenderMessage] = useState("");
    const [rating, setRatingState] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [providerContactName, setProviderContactName] = useState("");
    const [website, setWebsite] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [publishScope, setPublishScope] = useState("Full Trust Circle");
    const [selectedTrustCircles, setSelectedTrustCircles] = useState([]);
    const [internalUserTrustCircles, setInternalUserTrustCircles] = useState(
        []
    );
    const [internalTrustCirclesLoading, setInternalTrustCirclesLoading] =
        useState(false);
    const [internalTrustCirclesError, setInternalTrustCirclesError] =
        useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [images, setImages] = useState([]);
    const [existingImages, setExistingImages] = useState([]);

    // Potential match handling state variables
    const [potentialMatch, setPotentialMatch] = useState(null);
    const [showMatchConfirmation, setShowMatchConfirmation] = useState(false);
    const [pendingRecommendationData, setPendingRecommendationData] = useState(null);

    // Google Places API state variables
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [googlePlaceId, setGooglePlaceId] = useState("");
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");

    // Add getImageSrc function
    const getImageSrc = (image) => {
        if (!image) return "";
        
        // Handle new CDN URL structure
        if (image.url) return image.url;
        
        // Handle GCS path structure (for photo import flow)
        if (image.gcs_path) {
            return `https://storage.googleapis.com/tried-and-trusted-images-production/${image.gcs_path}`;
        }
        
        // Handle old binary data structure for backward compatibility
        const imageData = image.data?.data || image.data;
        if (!imageData) return "";

        try {
            if (Array.isArray(imageData)) {
                const bytes = new Uint8Array(imageData);
                const binary = bytes.reduce(
                    (acc, byte) => acc + String.fromCharCode(byte),
                    ""
                );
                const base64String = window.btoa(binary);
                return `data:${image.contentType};base64,${base64String}`;
            }

            if (typeof imageData === "string") {
                return `data:${image.contentType};base64,${imageData}`;
            }

            return "";
        } catch (error) {
            console.error("Error converting image data:", error);
            return "";
        }
    };

    useEffect(() => {
        if (recommendationToEdit) {
            // console.log("=== COMPONENT LOAD DEBUG ===");
            // console.log("EditRecommendationModal: Received recommendationToEdit:", recommendationToEdit);
            // console.log("EditRecommendationModal: Images in recommendationToEdit:", recommendationToEdit.images);
            // console.log("EditRecommendationModal: Images type:", typeof recommendationToEdit.images);
            // console.log("EditRecommendationModal: Images isArray:", Array.isArray(recommendationToEdit.images));
            
            setBusinessName(recommendationToEdit.business_name || "");
            setRecommenderMessage(
                recommendationToEdit.recommender_message || ""
            );
            setRatingState(
                recommendationToEdit.initial_rating ||
                    recommendationToEdit.rating ||
                    0
            );
            setTags(recommendationToEdit.tags || []);
            setProviderContactName(
                recommendationToEdit.provider_contact_name ||
                    recommendationToEdit.business_contact ||
                    ""
            );
            setWebsite(recommendationToEdit.website || "");
            setPhoneNumber(recommendationToEdit.phone_number || "");
            
            // Populate Google Place data if available
            setGooglePlaceId(recommendationToEdit.google_place_id || "");
            setLatitude(recommendationToEdit.latitude || null);
            setLongitude(recommendationToEdit.longitude || null);
            setStreetAddress(recommendationToEdit.street_address || "");
            setCity(recommendationToEdit.city || "");
            setState(recommendationToEdit.state || "");
            setZipCode(recommendationToEdit.zip_code || "");
            
            let initialPublishScope = "Full Trust Circle";
            let initialSelectedCircles = [];

            // Enhanced visibility logic that considers both visibility field and trust_circle_ids
            const visibility = recommendationToEdit.visibility || "connections";
            const trustCircleIds = Array.isArray(recommendationToEdit.trust_circle_ids) 
                ? recommendationToEdit.trust_circle_ids.filter(id => id) // Remove null/undefined values
                : [];

            if (visibility === "public") {
                initialPublishScope = "Public";
            } else if ((trustCircleIds || []).length > 0) {
                // If there are specific trust circles, it's "Specific Trust Circles" regardless of visibility field
                initialPublishScope = "Specific Trust Circles";
                initialSelectedCircles = trustCircleIds;
            } else if (visibility === "connections" || !visibility) {
                // Default to Full Trust Circle for connections visibility or unspecified
                initialPublishScope = "Full Trust Circle";
            } else if (visibility === "communities") {
                // Legacy communities visibility - treat as Full Trust Circle unless specific circles are specified
                initialPublishScope = "Full Trust Circle";
            } else {
                // Fallback to Full Trust Circle for any other cases
                initialPublishScope = "Full Trust Circle";
            }

            setPublishScope(initialPublishScope);
            setSelectedTrustCircles(initialSelectedCircles);

            setMessage("");
            setHoverRating(0);
            setTagInput("");
            
            // FIXED: Always set existing images from the recommendation data
            // This ensures we preserve the current images when editing
            const currentImages = recommendationToEdit.images || [];
            // console.log("EditRecommendationModal: Setting existingImages to:", currentImages);
            // console.log("EditRecommendationModal: existingImages type:", typeof currentImages);
            // console.log("EditRecommendationModal: existingImages isArray:", Array.isArray(currentImages));
            
            // Debug: Check if we need to look for images in other fields
            if (!currentImages || currentImages.length === 0) {
                // console.log("EditRecommendationModal: No images found in recommendationToEdit.images");
                // console.log("EditRecommendationModal: Checking other possible image fields:");
                // console.log("  - visible_reviews:", recommendationToEdit.visible_reviews);
                // console.log("  - review_images:", recommendationToEdit.review_images);
                // console.log("  - All keys in recommendationToEdit:", Object.keys(recommendationToEdit));
                
                // Try to find images in visible_reviews if available
                if (recommendationToEdit.visible_reviews && Array.isArray(recommendationToEdit.visible_reviews)) {
                    const imagesFromReviews = recommendationToEdit.visible_reviews
                        .filter(review => review.images && Array.isArray(review.images))
                        .flatMap(review => review.images);
                    
                    if (imagesFromReviews.length > 0) {
                        // console.log("EditRecommendationModal: Found images in visible_reviews:", imagesFromReviews);
                        setExistingImages(imagesFromReviews);
                        return;
                    }
                }
            }
            
            setExistingImages(currentImages);
            
            // Also log the images state initialization
            // console.log("EditRecommendationModal: Initial images state:", images);
            // console.log("EditRecommendationModal: Initial existingImages state:", existingImages);
        }
    }, [recommendationToEdit]);

    useEffect(() => {
        setInternalUserTrustCircles(userTrustCirclesProp || []);
        setInternalTrustCirclesLoading(trustCirclesLoadingProp || false);
        setInternalTrustCirclesError(trustCirclesErrorProp || "");
    }, [userTrustCirclesProp, trustCirclesLoadingProp, trustCirclesErrorProp]);

    useEffect(() => {
        if (
            publishScope === "Specific Trust Circles" &&
            (selectedTrustCircles || []).length === 0
        ) {
            (async () => {
                setInternalTrustCirclesLoading(true);
                setInternalTrustCirclesError("");
                try {
                    const circles = await fetchUserTrustCirclesFunc();
                    setInternalUserTrustCircles(circles || []);
                } catch (err) {
                    setInternalTrustCirclesError(
                        err.message || "Could not load trust circles."
                    );
                    setInternalUserTrustCircles([]);
                } finally {
                    setInternalTrustCirclesLoading(false);
                }
            })();
        }
    }, [
        publishScope,
        selectedTrustCircles,
        internalUserTrustCircles,
        internalTrustCirclesLoading,
        internalTrustCirclesError,
        fetchUserTrustCirclesFunc,
    ]);

    const handleStarClick = (n) => setRatingState(n);
    const handleTagKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            processTagInput();
        }
    };

    const processTagInput = () => {
        if (!tagInput.trim()) return;
        const newTags = tagInput
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag && !(tags || []).includes(tag));
        if (newTags.length > 0) {
            setTags([...(tags || []), ...newTags]);
        }
        setTagInput("");
    };

    const handleTagInputBlur = () => {
        if (tagInput.includes(",")) {
            processTagInput();
        }
    };

    const removeTag = (tagToRemove) =>
        setTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
    const handlePublishScopeChange = (e) => {
        const newScope = e.target.value;
        setPublishScope(newScope);
        if (newScope !== "Specific Trust Circles") setSelectedTrustCircles([]);
    };
    const handleTrustCircleToggle = (circleId) =>
        setSelectedTrustCircles((prev) =>
            prev.includes(circleId)
                ? prev.filter((id) => id !== circleId)
                : [...prev, circleId]
        );

    const handleImageSelect = (event) => {
        // console.log("=== IMAGE SELECTION DEBUG ===");
        // console.log("handleImageSelect called with:", {
        //     event: event,
        //     files: event.target.files,
        //     filesLength: event.target.files?.length
        // });
        
        const files = Array.from(event.target.files);

        // Add safety check to ensure existingImages is always an array
        const currentExistingImages = existingImages || [];
        const currentImages = images || [];
        
        // console.log("Current state before adding images:", {
        //     currentExistingImages: currentExistingImages,
        //     currentImages: currentImages,
        //     totalImages: currentExistingImages.length + currentImages.length + files.length
        // });

        if (currentExistingImages.length + currentImages.length + files.length > 5) {
            setMessage("error:Maximum 5 images allowed");
            return;
        }

        files.forEach((file, index) => {
            // console.log(`Processing file ${index}:`, {
            //     file: file,
            //     fileName: file.name,
            //     fileSize: file.size,
            //     fileType: file.type
            // });
            
            if (file.size > 5 * 1024 * 1024) {
                setMessage("error:Images must be under 5MB");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const newImage = {
                    id: Date.now() + Math.random(),
                    preview: reader.result,
                    file: file,
                };
                // console.log(`Created new image object:`, newImage);
                
                setImages((prev) => {
                    const newImages = [...(prev || []), newImage];
                    // console.log("Updated images state:", {
                    //     prev: prev,
                    //     newImages: newImages,
                    //     newImagesLength: newImages.length
                    // });
                    return newImages;
                });
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (imageId, isExisting = false) => {
        // console.log("=== IMAGE REMOVAL DEBUG ===");
        // console.log("removeImage called with:", {
        //     imageId: imageId,
        //     isExisting: isExisting,
        //     currentImages: images,
        //     currentExistingImages: existingImages
        // });
        if (isExisting) {
            setExistingImages((prev) =>
                (prev || []).filter((img) => img.id !== imageId)
            );
        } else {
            setImages((prev) => (prev || []).filter((img) => img.id !== imageId));
        }
        // console.log("State after removal:", {
        //     currentImages: images,
        //     currentExistingImages: existingImages
        // });
    };

    // Handle place selection from Google Places API
    const handlePlaceSelect = (place) => {
        setSelectedPlace(place);

        // Extract and autofill contact information from place details
        if (place.details) {
            // Autofill phone number if available
            if (place.details.internationalPhoneNumber) {
                setPhoneNumber(place.details.internationalPhoneNumber);
            }

            // Autofill website if available
            if (place.details.websiteUri) {
                setWebsite(place.details.websiteUri);
            }

            // Store address information silently
            if (place.addressInfo) {
                setStreetAddress(place.addressInfo.street_address || "");
                setCity(place.addressInfo.city || "");
                setState(place.addressInfo.state || "");
                setZipCode(place.addressInfo.zip_code || "");
            }

            // Store Google Place ID
            if (place.google_place_id) {
                setGooglePlaceId(place.google_place_id);
            }

            // Store latitude and longitude
            if (place.latitude !== undefined) {
                setLatitude(place.latitude);
            }
            if (place.longitude !== undefined) {
                setLongitude(place.longitude);
            }
        }
    };

    // Handle business name changes (both from autocomplete and custom input)
    const handleBusinessNameChange = (value) => {
        setBusinessName(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        
        // Add comprehensive debugging for form submission
        // console.log("=== FORM SUBMISSION DEBUG ===");
        // console.log("Form state at submission:", {
        //     businessName: businessName,
        //     recommenderMessage: recommenderMessage,
        //     rating: rating,
        //     tags: tags,
        //     providerContactName: providerContactName,
        //     website: website,
        //     phoneNumber: phoneNumber,
        //     publishScope: publishScope,
        //     selectedTrustCircles: selectedTrustCircles,
        //     existingImages: existingImages,
        //     images: images,
        //     googlePlaceId: googlePlaceId,
        //     latitude: latitude,
        //     longitude: longitude,
        //     streetAddress: streetAddress,
        //     city: city,
        //     state: state,
        //     zipCode: zipCode
        // });
        
        // Debug image arrays specifically
        // console.log("=== IMAGE ARRAYS DEBUG ===");
        // console.log("existingImages:", {
        //     value: existingImages,
        //     type: typeof existingImages,
        //     isArray: Array.isArray(existingImages),
        //     length: existingImages?.length,
        //     keys: existingImages ? Object.keys(existingImages) : 'N/A'
        // });
        // console.log("images:", {
        //     value: images,
        //     type: typeof images,
        //     isArray: Array.isArray(images),
        //     length: images?.length,
        //     keys: images ? Object.keys(images) : 'N/A'
        // });
        
        // FIXED: Ensure we have the most up-to-date image data
        // If existingImages is empty but we have recommendationToEdit.images, use that
        let finalExistingImages = existingImages;
        if ((!existingImages || existingImages.length === 0) && recommendationToEdit?.images) {
            // console.log("=== FALLBACK IMAGES DEBUG ===");
            // console.log("existingImages is empty, falling back to recommendationToEdit.images:", recommendationToEdit.images);
            finalExistingImages = recommendationToEdit.images;
        }
        
        if (
            !businessName.trim() ||
            !recommenderMessage.trim() ||
            rating === 0
        ) {
            setMessage(
                "error:Provider Name, Your Experience, and Rating are required."
            );
            return;
        }
        if (
            publishScope === "Specific Trust Circles" &&
            (selectedTrustCircles || []).length === 0
        ) {
            setMessage("error:Select trust circles or change publish scope.");
            return;
        }
        setIsSubmitting(true);
        setMessage("");
        const formData = new FormData();

        const jsonData = {
            business_name: businessName.trim(),
            recommender_message: recommenderMessage.trim(),
            rating: rating,
            tags: tags,
            provider_contact_name: providerContactName.trim() || null,
            website: website.trim() || null,
            phone_number: phoneNumber.trim() || null,
            publish_scope: publishScope,
            ...(publishScope === "Specific Trust Circles" && {
                trust_circle_ids: selectedTrustCircles,
            }),
            // FIXED: Send only the existing images that should be preserved
            // The backend will add new uploaded images to this list
            existingImages: finalExistingImages || [],
            // Google Places API data
            google_place_id: googlePlaceId || null,
            latitude: latitude,
            longitude: longitude,
            street_address: streetAddress || null,
            city: city || null,
            state: state || null,
            zip_code: zipCode || null,
        };

        formData.append("data", JSON.stringify(jsonData));

        // Add debugging for image handling
        // console.log("=== IMAGE HANDLING DEBUG ===");
        // console.log("Image handling debug:", {
        //     existingImagesCount: (finalExistingImages || []).length,
        //     newImagesCount: (images || []).length,
        //     existingImages: finalExistingImages,
        //     newImages: images,
        //     // Add detailed image structure analysis
        //     existingImagesStructure: (finalExistingImages || []).map(img => ({
        //         id: img.id,
        //         hasGcsPath: !!img.gcs_path,
        //         hasUrl: !!img.url,
        //         hasData: !!img.data,
        //         contentType: img.contentType,
        //         keys: Object.keys(img)
        //     }))
        // });

        // Debug the forEach loop specifically
        // console.log("=== FOREACH LOOP DEBUG ===");
        // console.log("About to process images array:", {
        //     images: images,
        //     imagesType: typeof images,
        //     imagesIsArray: Array.isArray(images),
        //     imagesLength: images?.length,
        //     safeImages: images || []
        // });
        
        (images || []).forEach((image, index) => {
            // console.log(`Processing image ${index}:`, {
            //     image: image,
            //     imageType: typeof image,
            //     hasFile: !!image?.file,
            //     fileType: image?.file?.type,
            //     fileSize: image?.file?.size
            // });
            formData.append("images", image.file);
        });

        try {
            const res = await fetch(
                `${apiBaseUrl}/api/recommendations/${recommendationToEdit.id}?user_id=${clerkUserId}&email=${userEmail}`,
                {
                    method: "PUT",
                    body: formData,
                }
            );
            const responseData = await res.json();

            // Check if we got a potential match response
            if (!responseData.success && responseData.potentialMatch) {
                setPotentialMatch(responseData.potentialMatch);
                setPendingRecommendationData(responseData.recommendationData);
                setShowMatchConfirmation(true);
                setIsSubmitting(false);
                return;
            }

            if (!res.ok || !responseData.success)
                throw new Error(
                    responseData.message ||
                        responseData.error ||
                        `Failed to update. Status: ${res.status}`
                );
            setMessage("success:Recommendation updated successfully!");
            if (onSaveSuccess) onSaveSuccess(responseData);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            setMessage(`error:${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmMatch = async (useExisting) => {
        setIsSubmitting(true);
        setMessage("");

        const formData = new FormData();
        const jsonData = {
            ...pendingRecommendationData,
            useExistingProvider: useExisting,
            existingProviderId: useExisting ? potentialMatch.providerId : null,
        };

        formData.append("data", JSON.stringify(jsonData));

        // Add any new images
        images.forEach((image) => {
            formData.append("images", image.file);
        });

        try {
            const res = await fetch(
                `${apiBaseUrl}/api/recommendations/${recommendationToEdit.id}?user_id=${clerkUserId}&email=${userEmail}`,
                {
                    method: "PUT",
                    body: formData,
                }
            );
            const responseData = await res.json();

            if (!res.ok || !responseData.success)
                throw new Error(
                    responseData.message ||
                        responseData.error ||
                        `Failed to update. Status: ${res.status}`
                );

            setMessage("success:Recommendation updated successfully!");
            if (onSaveSuccess) onSaveSuccess(responseData);
            setShowMatchConfirmation(false);
            setPotentialMatch(null);
            setPendingRecommendationData(null);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            setMessage(`error:${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;
    const requiredFieldsComplete =
        businessName.trim() && recommenderMessage.trim() && rating > 0;

    return (
        <div className="profile-edit-modal-overlay">
            <div className="profile-edit-modal-content">
                <button
                    onClick={onClose}
                    className="profile-edit-modal-close-btn"
                >
                    &times;
                </button>
                <h2 className="profile-edit-modal-title">
                    Edit Your Recommendation
                </h2>
                <form
                    onSubmit={handleSubmit}
                    className="profile-edit-modal-form"
                >
                    <section className="profile-edit-modal-form-section">
                        <h3 className="profile-edit-modal-section-title">
                            <span className="profile-edit-modal-section-number">
                                1
                            </span>
                            Core Details
                        </h3>
                        <div className="profile-edit-modal-form-grid">
                            <div className="profile-edit-modal-form-group span-2">
                                <label htmlFor="editBusinessName">
                                    Service Provider Name *
                                </label>
                                <PlaceAutocompleteInput
                                    value={businessName}
                                    onChange={handleBusinessNameChange}
                                    onPlaceSelect={handlePlaceSelect}
                                    placeholder="Search for a business or enter name manually..."
                                    allowCustomInput={true}
                                    className="profile-edit-modal-form-input"
                                />
                            </div>
                            <div className="profile-edit-modal-form-group span-2">
                                <label htmlFor="editRecommendationBlurb">
                                    Your Experience *
                                </label>
                                <textarea
                                    id="editRecommendationBlurb"
                                    value={recommenderMessage}
                                    onChange={(e) =>
                                        setRecommenderMessage(e.target.value)
                                    }
                                    required
                                    rows={4}
                                />
                            </div>
                            <div className="profile-edit-modal-form-group span-2 rating-group">
                                <label>Your Rating *</label>
                                <div className="profile-edit-modal-star-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <EditModalStarDisplay
                                            key={n}
                                            active={
                                                n <= (hoverRating || rating)
                                            }
                                            onClick={() => handleStarClick(n)}
                                            onMouseEnter={() =>
                                                setHoverRating(n)
                                            }
                                            onMouseLeave={() =>
                                                setHoverRating(0)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                    <div className="profile-edit-modal-optional-section-wrapper visible">
                        <section className="profile-edit-modal-form-section">
                            <h3 className="profile-edit-modal-section-title">
                                <span className="profile-edit-modal-section-number">
                                    2
                                </span>
                                Additional Info
                            </h3>
                            <div className="profile-edit-modal-form-grid">
                                <div className="profile-edit-modal-form-group">
                                    <label htmlFor="editProviderContactName">
                                        <UserCircleIcon /> Provider Contact
                                    </label>
                                    <input
                                        id="editProviderContactName"
                                        type="text"
                                        value={providerContactName}
                                        onChange={(e) =>
                                            setProviderContactName(
                                                e.target.value
                                            )
                                        }
                                        placeholder="e.g., Jane Doe"
                                    />
                                </div>
                                <div className="profile-edit-modal-form-group">
                                    <label htmlFor="editWebsite">
                                        <GlobeAltIcon /> Website
                                    </label>
                                    <input
                                        id="editWebsite"
                                        type="url"
                                        value={website}
                                        onChange={(e) =>
                                            setWebsite(e.target.value)
                                        }
                                        placeholder="https://provider.com"
                                    />
                                </div>
                                <div className="profile-edit-modal-form-group span-2">
                                    <label htmlFor="editPhoneNumber">
                                        <PhoneIcon /> Phone Number
                                    </label>
                                    <input
                                        id="editPhoneNumber"
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) =>
                                            setPhoneNumber(e.target.value)
                                        }
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                                <div className="profile-edit-modal-form-group span-2 tag-input-group">
                                    <label htmlFor="editTags">
                                        <TagIcon /> Tags (Press Enter)
                                    </label>
                                    <input
                                        id="editTags"
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) =>
                                            setTagInput(e.target.value)
                                        }
                                        onKeyDown={handleTagKeyDown}
                                        onBlur={handleTagInputBlur}
                                        placeholder="e.g., reliable, fast"
                                    />
                                    <div className="profile-edit-modal-tag-container">
                                        {tags.map((tag, i) => (
                                            <span
                                                key={i}
                                                className="profile-edit-modal-tag-pill"
                                            >
                                                {tag}
                                                <span
                                                    className="remove-tag"
                                                    onClick={() =>
                                                        removeTag(tag)
                                                    }
                                                >
                                                    ×
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className="profile-edit-modal-form-section">
                            <h3 className="profile-edit-modal-section-title">
                                <span className="profile-edit-modal-section-number">
                                    3
                                </span>
                                Share With
                            </h3>
                            <div className="profile-edit-modal-publish-options-grid">
                                {EDIT_MODAL_PUBLISH_OPTIONS.map((option) => (
                                    <label
                                        key={option.value}
                                        className={`profile-edit-modal-publish-option ${
                                            publishScope === option.value
                                                ? "selected"
                                                : ""
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="editPublishScope"
                                            value={option.value}
                                            checked={
                                                publishScope === option.value
                                            }
                                            onChange={handlePublishScopeChange}
                                            className="sr-only"
                                        />
                                        <option.icon className="publish-icon" />
                                        <span>{option.label}</span>
                                        {publishScope === option.value && (
                                            <CheckCircleIcon className="selected-check" />
                                        )}
                                    </label>
                                ))}
                            </div>
                            {publishScope === "Specific Trust Circles" && (
                                <div className="profile-edit-modal-trust-circle-select-wrapper">
                                    <label className="trust-circle-label">
                                        Select specific circles:
                                    </label>
                                    {internalTrustCirclesLoading && (
                                        <div className="loading-trust-circles">
                                            <ArrowPathIcon className="animate-spin h-5 w-5 mr-2 inline" />{" "}
                                            Loading...
                                        </div>
                                    )}
                                    {internalTrustCirclesError &&
                                        !internalTrustCirclesLoading && (
                                            <div className="error-trust-circles">
                                                <XCircleIcon className="h-5 w-5 mr-2 inline text-red-500" />{" "}
                                                {internalTrustCirclesError}{" "}
                                                <button
                                                    type="button"
                                                    onClick={
                                                        fetchUserTrustCirclesFunc
                                                    }
                                                    className="retry-button ml-2"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                    {!internalTrustCirclesLoading &&
                                        !internalTrustCirclesError &&
                                        (internalUserTrustCircles || []).length > 0 && (
                                            <div className="profile-edit-modal-trust-circle-checkbox-group">
                                                {internalUserTrustCircles.map(
                                                    (circle) => (
                                                        <label
                                                            key={circle.id}
                                                            className="trust-circle-checkbox-item"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                value={
                                                                    circle.id
                                                                }
                                                                checked={(selectedTrustCircles || []).includes(
                                                                    circle.id
                                                                )}
                                                                onChange={() =>
                                                                    handleTrustCircleToggle(
                                                                        circle.id
                                                                    )
                                                                }
                                                            />
                                                            <span>
                                                                {circle.name}
                                                            </span>
                                                        </label>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    {!internalTrustCirclesLoading &&
                                        !internalTrustCirclesError &&
                                        (internalUserTrustCircles || []).length ===
                                            0 && (
                                            <p className="no-trust-circles-message">
                                                No communities found or could
                                                not load.
                                            </p>
                                        )}
                                </div>
                            )}
                        </section>
                    </div>
                    <section className="profile-edit-modal-form-section">
                        <h3 className="profile-edit-modal-section-title">
                            <span className="profile-edit-modal-section-number">
                                4
                            </span>
                            Images
                        </h3>
                        <div className="profile-edit-modal-image-upload-section">
                            <div
                                className="profile-edit-modal-image-dropzone"
                                onClick={() =>
                                    document
                                        .getElementById("edit-image-upload")
                                        .click()
                                }
                            >
                                <PhotoIcon className="profile-edit-modal-image-dropzone-icon" />
                                <span>Click to upload images (up to 5)</span>
                                <input
                                    type="file"
                                    id="edit-image-upload"
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                    multiple
                                    style={{ display: "none" }}
                                />
                            </div>

                            {(existingImages && existingImages.length > 0 ||
                                images && images.length > 0) && (
                                <div className="profile-edit-modal-image-preview-grid">
                                    {(existingImages || []).map((image) => (
                                        <div
                                            key={image.id}
                                            className="profile-edit-modal-image-preview-item"
                                        >
                                            <img
                                                src={getImageSrc(image)}
                                                alt="Preview"
                                            />
                                            <button
                                                type="button"
                                                className="profile-edit-modal-image-preview-remove"
                                                onClick={() =>
                                                    removeImage(image.id, true)
                                                }
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    {(images || []).map((image) => (
                                        <div
                                            key={image.id}
                                            className="profile-edit-modal-image-preview-item"
                                        >
                                            <img
                                                src={image.preview}
                                                alt="Preview"
                                            />
                                            <button
                                                type="button"
                                                className="profile-edit-modal-image-preview-remove"
                                                onClick={() =>
                                                    removeImage(image.id)
                                                }
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <p className="profile-edit-modal-upload-limit-text">
                                {5 - ((existingImages && existingImages.length) || 0) - ((images && images.length) || 0)}{" "}
                                more images allowed
                            </p>
                        </div>
                    </section>
                    <div className="profile-edit-modal-button-row">
                        <button
                            type="button"
                            className="profile-edit-modal-btn cancel-btn"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="profile-edit-modal-btn save-btn"
                            disabled={isSubmitting || !requiredFieldsComplete}
                        >
                            {isSubmitting ? "Saving..." : "Save Changes"}{" "}
                            <CheckCircleIcon />
                        </button>
                    </div>
                    {message && (
                        <div
                            className={`profile-edit-modal-message ${
                                message.startsWith("error:")
                                    ? "error"
                                    : "success"
                            }`}
                        >
                            {message.startsWith("error:") ? (
                                <XCircleIcon />
                            ) : (
                                <CheckCircleIcon />
                            )}
                            <span>
                                {message.substring(message.indexOf(":") + 1)}
                            </span>
                        </div>
                    )}
                </form>
            </div>

            {/* Potential Match Confirmation Modal */}
            {showMatchConfirmation && potentialMatch && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="match-confirmation">
                            <h3>Similar Business Found</h3>
                            <p>
                                We found something that looks similar to what you're recommending:{" "}
                                <strong>{potentialMatch.businessName}</strong>
                            </p>
                            <p className="modal-question">
                                Are you recommending this?
                            </p>
                            <div className="success-modal-actions">
                                <button
                                    type="button"
                                    className="btn modal-btn"
                                    onClick={() => handleConfirmMatch(false)}
                                    disabled={isSubmitting}
                                >
                                    No, this is a different one.
                                </button>
                                <button
                                    type="button"
                                    className="btn modal-btn primary"
                                    onClick={() => handleConfirmMatch(true)}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Updating..." : "Yes, that's the one."}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditRecommendationModal;
